/**
 * BackgroundJobs — client-side scheduled maintenance tasks.
 *
 * Runs periodic cleanup and retry jobs using setInterval.
 * Each job is independently cancellable. Jobs:
 * - retryFailedEmails: re-process failed email queue items
 * - cleanupExpiredSignedUrls: no-op placeholder (URLs expire server-side)
 * - deleteAbandonedUploads: cleans orphaned local provider entries
 * - archiveOldAuditLogs: marks old audit records for archival
 * - scheduledHealthChecks: runs health check and logs results
 */

import { emailQueue } from '@/lib/email/EmailQueue';
import { emailService } from '@/lib/email/EmailService';
import { healthService } from '@/lib/HealthService';
import { analyticsService } from '@/lib/AnalyticsService';
import { LoggerFactory } from '@/lib/logging';

const logger = LoggerFactory.create();

type JobId = 'retryFailedEmails' | 'cleanupExpiredSignedUrls' | 'deleteAbandonedUploads' | 'archiveOldAuditLogs' | 'scheduledHealthChecks';

interface JobConfig {
  id: JobId;
  intervalMs: number;
  fn: () => Promise<void>;
}

class BackgroundJobRunner {
  private timers = new Map<JobId, ReturnType<typeof setInterval>>();
  private running = new Set<JobId>();

  start(config: JobConfig): void {
    if (this.timers.has(config.id)) return;
    const timer = setInterval(() => {
      void this.runOnce(config);
    }, config.intervalMs);
    this.timers.set(config.id, timer);
    logger.info(`[background-jobs] Started: ${config.id}`, { intervalMs: config.intervalMs });
  }

  stop(id: JobId): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearInterval(timer);
      this.timers.delete(id);
      logger.info(`[background-jobs] Stopped: ${id}`);
    }
  }

  stopAll(): void {
    for (const id of this.timers.keys()) {
      this.stop(id);
    }
  }

  private async runOnce(config: JobConfig): Promise<void> {
    if (this.running.has(config.id)) return;
    this.running.add(config.id);
    try {
      await config.fn();
    } catch (err) {
      logger.error(`[background-jobs] ${config.id} failed`, {
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      this.running.delete(config.id);
    }
  }
}

const runner = new BackgroundJobRunner();

const jobs: JobConfig[] = [
  {
    id: 'retryFailedEmails',
    intervalMs: 5 * 60 * 1000,
    fn: async () => {
      const failed = emailQueue.getFailed();
      if (failed.length === 0) return;
      logger.info(`[background-jobs] Retrying ${failed.length} failed emails`);
      for (const item of failed) {
        void emailService.send(
          item.message.to,
          item.message.metadata.template as never,
          {},
          item.message.metadata.priority as never,
          { recipientUserId: item.message.metadata.recipientUserId, applicationId: item.message.metadata.applicationId },
        ).catch((err) => {
          logger.error(`[background-jobs] Email retry failed for ${item.id}`, {
            error: err instanceof Error ? err.message : String(err),
            to: item.message.to,
          });
        });
      }
      analyticsService.track('email_retry', { count: failed.length });
    },
  },
  {
    id: 'cleanupExpiredSignedUrls',
    intervalMs: 60 * 60 * 1000,
    fn: async () => {
      // Signed URLs expire server-side — no client cleanup needed.
      // This job exists as a hook for future client-side URL cache cleanup.
      logger.debug('[background-jobs] Signed URL cleanup — no-op (server-side expiry)');
    },
  },
  {
    id: 'deleteAbandonedUploads',
    intervalMs: 30 * 60 * 1000,
    fn: async () => {
      // LocalStorageProvider entries are in-memory and cleared on reload.
      // In production, abandoned multipart uploads are cleaned by Supabase.
      logger.debug('[background-jobs] Abandoned upload cleanup — delegated to provider');
    },
  },
  {
    id: 'archiveOldAuditLogs',
    intervalMs: 24 * 60 * 60 * 1000,
    fn: async () => {
      // Audit logs are immutable and persisted to the database.
      // Archival would be a server-side operation (edge function or DB job).
      logger.debug('[background-jobs] Audit log archival — server-side operation');
    },
  },
  {
    id: 'scheduledHealthChecks',
    intervalMs: 10 * 60 * 1000,
    fn: async () => {
      const report = await healthService.checkAll();
      if (report.overall !== 'healthy') {
        logger.warn(`[background-jobs] Health check: ${report.overall}`, {
          checks: report.checks.filter((c) => c.status !== 'healthy').map((c) => `${c.name}: ${c.message}`),
        });
      }
      analyticsService.track('page_view', { healthStatus: report.overall });
    },
  },
];

export function startBackgroundJobs(): void {
  for (const job of jobs) {
    runner.start(job);
  }
}

export function stopBackgroundJobs(): void {
  runner.stopAll();
}

export function stopJob(id: JobId): void {
  runner.stop(id);
}
